import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Check, Home } from 'lucide-react';
import { siteConfig } from '../data/site-content';

const BookTid = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Lock body scroll when EasyPractice modal is open
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    }
  }, [isModalOpen]);

  // Load EasyPractice iframe script when modal opens
  useEffect(() => {
    if (isModalOpen) {
      const scriptId = 'easypractice-iframe-script';
      if (!document.getElementById(scriptId)) {
        const script = document.createElement('script');
        script.id = scriptId;
        script.src = 'https://system.easypractice.net/js/templates/online-book.iframe.js';
        script.async = true;
        document.body.appendChild(script);
      }
    }
  }, [isModalOpen]);

  const handleDayToggle = (day) => {
    if (selectedDays.includes(day)) {
      setSelectedDays(selectedDays.filter(d => d !== day));
    } else {
      setSelectedDays([...selectedDays, day]);
    }
  };

  const handleCallbackSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(false);
    setErrorMessage('');

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    // Antispam Honeypot check
    if (data.website_url) {
      setIsSubmitted(true);
      return;
    }

    if (selectedDays.length === 0) {
      setErrorMessage('Vælg venligst mindst én hverdag, der passer dig.');
      return;
    }

    if (!selectedTimeSlot) {
      setErrorMessage('Vælg venligst et tidsrum, der passer dig.');
      return;
    }

    setIsSubmitting(true);

    const payload = {
      ...data,
      onskede_dage: selectedDays.join(', '),
      foretrukket_tidsrum: selectedTimeSlot,
      form_source: 'Bestil tid - Ring mig op (V7 flow)'
    };

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        const errData = await response.json().catch(() => ({}));
        setErrorMessage(errData.message || 'Der opstod en fejl. Prøv venligst igen eller kontakt mig direkte på telefon 27 20 46 71.');
      }
    } catch (error) {
      console.error('Callback form submission error:', error);
      setErrorMessage('Der opstod en netværksfejl. Kontrollér din internetforbindelse og prøv igen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openBookingModal = () => {
    setIsModalOpen(true);
  };

  const closeBookingModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <Helmet>
        <title>Bestil tid hos Lars Nissen Corell – Psykoterapeut MPF | Core Hypnose</title>
        <meta name="description" content="Bestil din første behandlingssession hos Lars Nissen Corell – Psykoterapeut MPF. Pris 375 kr. for 50 min. Vælg mellem klinik i Valby eller online. Ingen binding." />
        <meta property="og:title" content="Bestil tid hos Lars Nissen Corell – Psykoterapeut MPF | Core Hypnose" />
        <meta property="og:description" content="Bestil din første behandlingssession hos Lars Nissen Corell – Psykoterapeut MPF. Pris 375 kr. for 50 min. Vælg mellem klinik i Valby eller online. Ingen binding." />
        <link rel="canonical" href="https://corehypnose.dk/bestil-tid/" />
      </Helmet>

      {/* Hero Section */}
      <section className="py-12 md:py-20 bg-[#FAF7F2] border-b border-[#E8DDCE]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            
            {/* Left Column: Heading, intro notes and portrætstøttebillede */}
            <div className="lg:col-span-5 space-y-6">
              <div>
                <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest block mb-3">
                  Tag næste skridt
                </span>
                <h1 className="text-3xl md:text-5xl font-serif text-[#123F13] leading-tight mb-4">
                  Hvordan vil du<br /> gerne starte?
                </h1>
                <p className="text-lg md:text-xl text-[#3B352F]/80 leading-relaxed">
                  Du kan booke din første behandlingssession med det samme, bede mig ringe dig op først eller ringe direkte og se, om jeg er ledig.
                </p>
              </div>

              {/* Faktaboks */}
              <div className="bg-white border border-[#E8DDCE] p-5 rounded-xl text-sm leading-relaxed text-[#3B352F]">
                <p className="font-medium text-[#123F13]">
                  Første behandlingssession er for nye klienter, varer 50 minutter og koster 375 kr. Du kan vælge Valby eller online.
                </p>
              </div>

              {/* Det brede portrætbillede: Core hypnose nr.1.png (max ca. 420-500px, roligt støttebillede) */}
              <div className="pt-2">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=90,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/core-hypnose-nr-1.png" 
                  alt="Lars Nissen Corell – Psykoterapeut MPF" 
                  className="w-full max-w-[460px] rounded-xl shadow-sm border border-[#E8DDCE]/80 object-cover aspect-[4/3]"
                  loading="eager"
                />
              </div>
            </div>

            {/* Right Column: De tre kontaktveje i prioriteret rækkefølge */}
            <div className="lg:col-span-7 flex flex-col gap-6">
              
              {/* 1. Direkte booking (PRIMÆR & VISUELT FREMHÆVET) */}
              <div 
                className="relative bg-white rounded-2xl border-2 border-solid border-[#234c3f] p-6 md:p-8 overflow-hidden"
                style={{ borderColor: '#234c3f', borderWidth: '2px' }}
              >
                <div className="absolute top-0 right-0 bg-[#123F13] text-[#fcfbf9] text-[11px] font-bold uppercase tracking-wider px-3.5 py-1.5 rounded-bl-xl shadow-sm">
                  Mest direkte
                </div>

                <div className="flex items-center gap-2.5 text-[#123F13] font-bold text-xs uppercase tracking-wider mb-2">
                  <span className="w-6 h-6 rounded-full bg-[#123F13] text-white flex items-center justify-center text-xs">1</span>
                  Jeg vil booke en tid nu
                </div>

                <h2 className="text-2xl md:text-3xl font-serif text-[#123F13] mb-3">
                  Book din første behandlingssession
                </h2>

                <p className="text-[#3B352F]/80 text-sm md:text-base leading-relaxed mb-5">
                  Vælg Valby eller online, find en dato og et tidspunkt, der passer dig, og gennemfør din booking med det samme.
                </p>

                {/* Punkter med grønt checkmark */}
                <div className="flex flex-wrap items-center justify-between gap-y-3 gap-x-6 mb-5">
                  <div className="flex items-center gap-2 text-sm md:text-base font-semibold text-[#123F13]">
                    <Check className="w-5 h-5 text-[#24732b] shrink-0 stroke-[2.5]" aria-hidden="true" />
                    <span className="whitespace-nowrap">375 kr.</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm md:text-base font-semibold text-[#123F13]">
                    <Check className="w-5 h-5 text-[#24732b] shrink-0 stroke-[2.5]" aria-hidden="true" />
                    <span className="whitespace-nowrap">50 minutter</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm md:text-base font-semibold text-[#123F13]">
                    <Check className="w-5 h-5 text-[#24732b] shrink-0 stroke-[2.5]" aria-hidden="true" />
                    <span className="whitespace-nowrap">Kun nye klienter</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm md:text-base font-semibold text-[#123F13]">
                    <Check className="w-5 h-5 text-[#24732b] shrink-0 stroke-[2.5]" aria-hidden="true" />
                    <span className="whitespace-nowrap">Ingen binding</span>
                  </div>
                </div>

                <p className="text-xs text-[#8C2F12] font-medium mb-6">
                  Bemærk: Introprisen på 375 kr. gælder ikke rygestop.
                </p>

                <button
                  type="button"
                  onClick={openBookingModal}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#123F13] hover:bg-[#123F13]/90 text-white px-7 py-4 rounded-xl font-bold text-base shadow-lg shadow-[#123F13]/20 hover:shadow-none hover:translate-y-0.5 transition-all cursor-pointer"
                >
                  <i className="fa-regular fa-calendar-check text-lg" aria-hidden="true" />
                  <span>Book første behandlingssession</span>
                </button>
              </div>

              {/* 2. Callback */}
              <div className="bg-white rounded-2xl border border-[#E8DDCE] p-6 md:p-8 shadow-sm hover:border-[#123F13]/40 transition-colors">
                <div className="flex items-center gap-2.5 text-[#8C2F12] font-bold text-xs uppercase tracking-wider mb-2">
                  <span className="w-6 h-6 rounded-full bg-[#8C2F12] text-white flex items-center justify-center text-xs">2</span>
                  Jeg vil gerne ringes op først
                </div>

                <h3 className="text-xl md:text-2xl font-serif text-[#123F13] mb-2">
                  Få en kort afklaring først
                </h3>

                <p className="text-[#3B352F]/80 text-sm md:text-base leading-relaxed mb-6">
                  Har du spørgsmål eller er du usikker på, om behandlingen passer til dig, kan du bede mig ringe dig op. Samtalen er til kort afklaring og booking.
                </p>

                <a
                  href="#callback-section"
                  className="inline-flex items-center justify-center gap-2 border-2 border-[#123F13] text-[#123F13] hover:bg-[#123F13] hover:text-white px-6 py-3 rounded-xl font-bold text-sm transition-all"
                >
                  <i className="fa-solid fa-phone-arrow-down-left text-sm" aria-hidden="true" />
                  <span>Bed mig ringe dig op</span>
                </a>
              </div>

              {/* 3. Ring direkte */}
              <div className="bg-white rounded-2xl border border-[#E8DDCE] p-6 md:p-8 shadow-sm hover:border-[#123F13]/40 transition-colors">
                <div className="flex items-center gap-2.5 text-[#3B352F]/70 font-bold text-xs uppercase tracking-wider mb-2">
                  <span className="w-6 h-6 rounded-full bg-[#3B352F]/20 text-[#3B352F] flex items-center justify-center text-xs">3</span>
                  Ring nu og se, om jeg er ledig
                </div>

                <h3 className="text-xl md:text-2xl font-serif text-[#123F13] mb-2">
                  Har du lyst til at prøve nu?
                </h3>

                <p className="text-[#3B352F]/80 text-sm md:text-base leading-relaxed mb-6">
                  Ring på <span className="font-bold text-[#123F13]">27 20 46 71</span> og se, om jeg tilfældigvis er ledig. Hvis jeg ikke kan tage telefonen, kan du altid booke eller bede mig ringe dig op.
                </p>

                <a
                  href="tel:+4527204671"
                  className="inline-flex items-center justify-center gap-2 bg-[#FAF7F2] hover:bg-[#123F13] text-[#123F13] hover:text-white border border-[#E8DDCE] px-6 py-3 rounded-xl font-bold text-sm transition-all shadow-sm"
                >
                  <i className="fa-solid fa-phone text-sm" aria-hidden="true" />
                  <span>Ring 27 20 46 71</span>
                </a>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Fakta-bånd / Nøgletal */}
      <section className="py-6 bg-white border-b border-[#E8DDCE]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center md:justify-between gap-y-3 gap-x-6 text-sm text-[#3B352F]">
            <div className="flex items-center gap-2 font-medium">
              <i className="fa-solid fa-check text-[#123F13]" aria-hidden="true" />
              <span>375 kr.</span>
            </div>
            <span className="hidden md:inline text-[#E8DDCE]">·</span>
            <div className="flex items-center gap-2 font-medium">
              <i className="fa-regular fa-clock text-[#123F13]" aria-hidden="true" />
              <span>50 minutter</span>
            </div>
            <span className="hidden md:inline text-[#E8DDCE]">·</span>
            <div className="flex items-center gap-2 font-medium">
              <i className="fa-solid fa-location-dot text-[#123F13]" aria-hidden="true" />
              <span>Valby eller online</span>
            </div>
            <span className="hidden md:inline text-[#E8DDCE]">·</span>
            <div className="flex items-center gap-2 font-medium">
              <i className="fa-solid fa-circle-check text-[#123F13]" aria-hidden="true" />
              <span>Ingen binding</span>
            </div>
            <span className="hidden md:inline text-[#E8DDCE]">·</span>
            <div className="flex items-center gap-2 font-medium">
              <i className="fa-solid fa-credit-card text-[#123F13]" aria-hidden="true" />
              <span>Betaling inden sessionen starter</span>
            </div>
          </div>
        </div>
      </section>

      {/* Første behandlingssession detaljesektion med 375 kr.-grafik til venstre */}
      <section className="py-14 md:py-20 bg-white border-b border-[#E8DDCE]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-stretch">
            {/* Grafik til venstre */}
            <div className="lg:col-span-5 flex items-center justify-center lg:justify-end">
              <div className="w-full max-w-[420px]">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=840,quality=95,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/core-hypnose-fa-rste-behandling-375.png" 
                  alt="Core Hypnose Første Behandling 375,-" 
                  className="w-full h-auto object-contain mx-auto drop-shadow-sm rounded-xl"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Tekstcontainer til højre */}
            <div className="lg:col-span-7 flex flex-col justify-between text-center lg:text-left">
              <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest block mb-2">
                Første behandlingssession
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-4">
                Et enkelt første skridt
              </h2>
              <p className="text-base md:text-lg text-[#3B352F]/80 leading-relaxed mb-8 max-w-2xl lg:max-w-none">
                Første behandlingssession er for nye klienter, der ønsker at komme i gang uden at binde sig til et længere forløb. Du vælger selv, om sessionen skal foregå i Valby eller online.
              </p>

              <div className="grid grid-cols-2 gap-x-6 gap-y-3 mb-6 max-w-md mx-auto lg:mx-0">
                <div className="flex items-center justify-start gap-2.5 text-[#123F13]">
                  <i className="fa-solid fa-check text-[#8C2F12] text-sm shrink-0" aria-hidden="true" />
                  <span className="font-serif text-base sm:text-lg font-bold">50 minutter</span>
                </div>
                <div className="flex items-center justify-start gap-2.5 text-[#123F13]">
                  <i className="fa-solid fa-check text-[#8C2F12] text-sm shrink-0" aria-hidden="true" />
                  <span className="font-serif text-base sm:text-lg font-bold">375 kr.</span>
                </div>
                <div className="flex items-center justify-start gap-2.5 text-[#123F13]">
                  <i className="fa-solid fa-check text-[#8C2F12] text-sm shrink-0" aria-hidden="true" />
                  <span className="font-serif text-base sm:text-lg font-bold">Valby eller online</span>
                </div>
                <div className="flex items-center justify-start gap-2.5 text-[#123F13]">
                  <i className="fa-solid fa-check text-[#8C2F12] text-sm shrink-0" aria-hidden="true" />
                  <span className="font-serif text-base sm:text-lg font-bold">Ingen binding</span>
                </div>
              </div>

              <p className="text-xs sm:text-sm text-[#8C2F12] font-medium mb-8">
                Bemærk: Introprisen på 375 kr. gælder ikke rygestop.
              </p>

              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4">
                <button
                  type="button"
                  onClick={openBookingModal}
                  className="inline-flex items-center gap-2 bg-[#123F13] hover:bg-[#123F13]/90 text-white px-6 py-3.5 rounded-xl font-bold text-sm shadow-md shadow-[#123F13]/20 transition-all cursor-pointer"
                >
                  <i className="fa-regular fa-calendar-check text-base" aria-hidden="true" />
                  <span>Book første behandlingssession</span>
                </button>

                <a
                  href="#callback-section"
                  className="inline-flex items-center gap-2 border-2 border-[#123F13] text-[#123F13] hover:bg-[#FAF7F2] px-6 py-3 rounded-xl font-bold text-sm transition-all"
                >
                  <i className="fa-solid fa-phone-arrow-down-left text-sm" aria-hidden="true" />
                  <span>Bed mig ringe dig op</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Callback Section (#callback-section) */}
      <section id="callback-section" className="py-14 md:py-20 bg-[#FAF7F2] border-b border-[#E8DDCE] scroll-mt-20">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest block mb-2">
              Vil du hellere tale først?
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-3">
              Bed mig ringe dig op
            </h2>
            <p className="text-[#3B352F]/80 text-base md:text-lg max-w-xl mx-auto">
              Udfyld den korte formular og vælg de hverdage og det tidsrum, der passer dig bedst. Du må gerne vælge flere dage.
            </p>
          </div>

          {isSubmitted ? (
            <div className="bg-white p-8 md:p-10 rounded-2xl border-2 border-[#123F13] text-center shadow-md">
              <div className="w-14 h-14 bg-[#123F13]/10 text-[#123F13] rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
                <i className="fa-solid fa-check" aria-hidden="true" />
              </div>
              <h3 className="text-2xl font-serif text-[#123F13] mb-2">Tak for din henvendelse</h3>
              <p className="text-[#3B352F]/80 text-sm md:text-base mb-6 leading-relaxed">
                Jeg har modtaget dine ønsker og ringer dig op inden for det valgte tidsrum på en af de hverdage, du har markeret.
              </p>
              <button
                type="button"
                onClick={() => {
                  setIsSubmitted(false);
                  setSelectedDays([]);
                  setSelectedTimeSlot('');
                }}
                className="inline-flex items-center gap-2 text-[#123F13] font-bold text-sm underline hover:text-[#123F13]/80"
              >
                Send en ny anmodning
              </button>
            </div>
          ) : (
            <form 
              onSubmit={handleCallbackSubmit} 
              className="bg-white p-6 sm:p-8 md:p-10 rounded-2xl border border-[#E8DDCE] shadow-md space-y-6"
            >
              {/* Antispam Honeypot field (mandatory hidden text input) */}
              <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
                <label htmlFor="website_url">Website URL (leave empty)</label>
                <input 
                  type="text" 
                  id="website_url" 
                  name="website_url" 
                  tabIndex={-1} 
                  autoComplete="off" 
                />
              </div>

              {errorMessage && (
                <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm flex items-start gap-3">
                  <i className="fa-solid fa-circle-exclamation text-red-500 mt-0.5" aria-hidden="true" />
                  <span>{errorMessage}</span>
                </div>
              )}

              {/* Basis kontaktfelter */}
              <div className="space-y-4">
                <div>
                  <label htmlFor="callback_navn" className="block text-sm font-semibold text-[#123F13] mb-1.5">
                    Navn <span className="text-[#8C2F12]">*</span>
                  </label>
                  <input
                    type="text"
                    id="callback_navn"
                    name="navn"
                    required
                    placeholder="Dit fulde navn"
                    className="w-full px-4 py-3 bg-[#FAF7F2]/50 border border-[#E8DDCE] rounded-xl text-sm md:text-base text-[#3B352F] focus:outline-none focus:ring-2 focus:ring-[#123F13] focus:border-transparent transition-all"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="callback_telefon" className="block text-sm font-semibold text-[#123F13] mb-1.5">
                      Telefon <span className="text-[#8C2F12]">*</span>
                    </label>
                    <input
                      type="tel"
                      id="callback_telefon"
                      name="telefon"
                      required
                      placeholder="F.eks. 27 20 46 71"
                      className="w-full px-4 py-3 bg-[#FAF7F2]/50 border border-[#E8DDCE] rounded-xl text-sm md:text-base text-[#3B352F] focus:outline-none focus:ring-2 focus:ring-[#123F13] focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label htmlFor="callback_email" className="block text-sm font-semibold text-[#123F13] mb-1.5">
                      E-mail <span className="text-[#8C2F12]">*</span>
                    </label>
                    <input
                      type="email"
                      id="callback_email"
                      name="email"
                      required
                      placeholder="din@email.dk"
                      className="w-full px-4 py-3 bg-[#FAF7F2]/50 border border-[#E8DDCE] rounded-xl text-sm md:text-base text-[#3B352F] focus:outline-none focus:ring-2 focus:ring-[#123F13] focus:border-transparent transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="callback_emne" className="block text-sm font-semibold text-[#123F13] mb-1.5">
                    Hvad ønsker du hjælp til? <span className="text-[#8C2F12]">*</span>
                  </label>
                  <select
                    id="callback_emne"
                    name="emne"
                    required
                    defaultValue=""
                    className="w-full px-4 py-3 bg-[#FAF7F2]/50 border border-[#E8DDCE] rounded-xl text-sm md:text-base text-[#3B352F] focus:outline-none focus:ring-2 focus:ring-[#123F13] focus:border-transparent transition-all"
                  >
                    <option value="" disabled>Vælg venligst et område</option>
                    <option value="Angst & Panik">Angst & Panik</option>
                    <option value="Stress & Tankemylder">Stress & Tankemylder</option>
                    <option value="Depression & Modløshed">Depression & Modløshed</option>
                    <option value="Søvnproblemer">Søvnproblemer</option>
                    <option value="OCD">OCD</option>
                    <option value="ADHD / Autismevenlig terapi">ADHD / Autismevenlig terapi</option>
                    <option value="Eksamens- & Præstationsangst">Eksamens- & Præstationsangst</option>
                    <option value="Flyskræk">Flyskræk</option>
                    <option value="Sorg & Tab">Sorg & Tab</option>
                    <option value="Parterapi & Relationer">Parterapi & Relationer</option>
                    <option value="Rygestop (fast pris)">Rygestop (fast pris)</option>
                    <option value="Andet">Andet</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="callback_beskrivelse" className="block text-sm font-semibold text-[#123F13] mb-1.5">
                    Kort beskrivelse <span className="text-[#3B352F]/50 font-normal">(valgfrit)</span>
                  </label>
                  <textarea
                    id="callback_beskrivelse"
                    name="beskrivelse"
                    rows={3}
                    placeholder="Skriv gerne et par ord om din situation..."
                    className="w-full px-4 py-3 bg-[#FAF7F2]/50 border border-[#E8DDCE] rounded-xl text-sm md:text-base text-[#3B352F] focus:outline-none focus:ring-2 focus:ring-[#123F13] focus:border-transparent transition-all"
                  />
                </div>
              </div>

              {/* Vælg hverdage (Checkboxes - multi select) */}
              <div className="pt-4 border-t border-[#E8DDCE]">
                <label className="block text-sm font-semibold text-[#123F13] mb-0.5">
                  Hvilke dage passer dig bedst? <span className="text-[#8C2F12]">*</span>
                </label>
                <p className="text-xs text-[#3B352F]/70 mb-3">Vælg gerne flere hverdage</p>

                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
                  {['Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag'].map((day) => {
                    const isChecked = selectedDays.includes(day);
                    return (
                      <label
                        key={day}
                        className={`flex items-center justify-center gap-2 p-3 rounded-xl border text-sm font-medium cursor-pointer transition-all ${
                          isChecked
                            ? 'bg-[#123F13] border-[#123F13] text-white shadow-sm'
                            : 'bg-[#FAF7F2] border-[#E8DDCE] text-[#3B352F] hover:bg-white'
                        }`}
                      >
                        <input
                          type="checkbox"
                          value={day}
                          checked={isChecked}
                          onChange={() => handleDayToggle(day)}
                          className="sr-only"
                        />
                        <span>{day}</span>
                        {isChecked && <i className="fa-solid fa-check text-xs" aria-hidden="true" />}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Vælg tidsrum (Radio buttons - single select) */}
              <div className="pt-2">
                <label className="block text-sm font-semibold text-[#123F13] mb-0.5">
                  Hvilket tidsrum passer dig bedst? <span className="text-[#8C2F12]">*</span>
                </label>
                <p className="text-xs text-[#3B352F]/70 mb-3">Vælg ét tidsrum</p>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                  {['10.00–12.00', '12.00–14.00', '14.00–16.00', '16.00–18.00'].map((slot) => {
                    const isChecked = selectedTimeSlot === slot;
                    return (
                      <label
                        key={slot}
                        className={`flex items-center justify-center gap-2 p-3 rounded-xl border text-sm font-medium cursor-pointer transition-all ${
                          isChecked
                            ? 'bg-[#123F13] border-[#123F13] text-white shadow-sm'
                            : 'bg-[#FAF7F2] border-[#E8DDCE] text-[#3B352F] hover:bg-white'
                        }`}
                      >
                        <input
                          type="radio"
                          name="tidsrum"
                          value={slot}
                          checked={isChecked}
                          onChange={() => setSelectedTimeSlot(slot)}
                          className="sr-only"
                        />
                        <span>{slot}</span>
                        {isChecked && <i className="fa-solid fa-circle-dot text-xs" aria-hidden="true" />}
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-[#3B352F]/70 mt-2.5 italic">
                  Jeg ringer inden for det valgte tidsrum på en af de hverdage, du har markeret.
                </p>
              </div>

              {/* Oplysende noter */}
              <div className="space-y-1.5 pt-2 text-xs sm:text-sm text-[#3B352F]/75">
                <p className="font-semibold text-[#123F13]">
                  Telefonsamtalen er til en kort afklaring og booking – ikke behandling.
                </p>
                <p>
                  Dine oplysninger behandles fortroligt og bruges kun til at kontakte dig om din henvendelse.
                </p>
              </div>

              {/* Submit CTA */}
              <div className="pt-3">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#123F13] hover:bg-[#123F13]/90 text-white py-4 px-6 rounded-xl font-bold text-base shadow-lg shadow-[#123F13]/20 transition-all flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                      <span>Sender anmodning...</span>
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-phone-arrow-down-left" aria-hidden="true" />
                      <span>Bed mig ringe dig op</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Tempo & tryghed */}
      <section className="py-14 md:py-20 bg-white border-b border-[#E8DDCE]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest block mb-2">
              Du bestemmer tempoet
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-4">
              Vælg det, der føles lettest
            </h2>
            <p className="text-[#3B352F]/80 text-base md:text-lg">
              Du behøver ikke beslutte dig for et helt forløb. Vælg bare det næste skridt, der passer bedst til dig.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12">
            <div className="p-6 md:p-8 rounded-2xl bg-[#FAF7F2] border border-[#E8DDCE]">
              <div className="w-10 h-10 rounded-full bg-[#123F13]/10 text-[#123F13] flex items-center justify-center font-bold text-base mb-4">
                ✓
              </div>
              <h3 className="text-lg md:text-xl font-serif text-[#123F13] mb-2 font-bold">Start i dit eget tempo</h3>
              <p className="text-sm text-[#3B352F]/80 leading-relaxed">
                Book med det samme, få en kort afklaring først eller ring direkte. Du vælger selv, hvordan kontakten skal starte.
              </p>
            </div>

            <div className="p-6 md:p-8 rounded-2xl bg-[#FAF7F2] border border-[#E8DDCE]">
              <div className="w-10 h-10 rounded-full bg-[#123F13]/10 text-[#123F13] flex items-center justify-center font-bold text-base mb-4">
                <Home className="w-5 h-5 text-[#123F13]" aria-hidden="true" />
              </div>
              <h3 className="text-lg md:text-xl font-serif text-[#123F13] mb-2 font-bold">Valby eller online</h3>
              <p className="text-sm text-[#3B352F]/80 leading-relaxed">
                Vælg den ramme, der passer bedst til dig. Første behandlingssession kan foregå i klinikken i Valby eller online.
              </p>
            </div>

            <div className="p-6 md:p-8 rounded-2xl bg-[#FAF7F2] border border-[#E8DDCE]">
              <div className="w-10 h-10 rounded-full bg-[#123F13]/10 text-[#123F13] flex items-center justify-center font-bold text-base mb-4">
                ○
              </div>
              <h3 className="text-lg md:text-xl font-serif text-[#123F13] mb-2 font-bold">Ingen binding</h3>
              <p className="text-sm text-[#3B352F]/80 leading-relaxed">
                Den første session er en selvstændig behandlingssession. Du binder dig ikke til et længere forløb ved at booke.
              </p>
            </div>
          </div>

          {/* Quick call reminder card */}
          <div className="max-w-5xl mx-auto bg-[#123F13] text-white p-8 md:p-10 rounded-2xl shadow-sm flex flex-col md:flex-row items-center justify-between gap-6 text-left">
            <div className="max-w-2xl">
              <h3 className="text-xl md:text-2xl font-serif text-white mb-2 font-bold">Vil du prøve at ringe nu?</h3>
              <p className="text-sm md:text-base text-white/90 leading-relaxed">
                Ring og se, om jeg er ledig. Hvis jeg ikke kan tage telefonen, kan du stadig booke direkte eller bede mig ringe dig op.
              </p>
            </div>
            <div className="shrink-0 w-full md:w-auto">
              <a
                href="tel:+4527204671"
                className="w-full md:w-auto justify-center inline-flex items-center gap-2.5 bg-white hover:bg-[#FAF7F2] text-[#123F13] px-7 py-3.5 rounded-xl font-bold text-sm transition-all shadow-sm"
              >
                <i className="fa-solid fa-phone text-sm text-[#123F13]" aria-hidden="true" />
                <span>Ring 27 20 46 71</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Praktiske links */}
      <section className="py-14 md:py-16 bg-[#FAF7F2] border-b border-[#E8DDCE]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest block mb-2">
            Vil du læse mere først?
          </span>
          <h2 className="text-2xl md:text-3xl font-serif text-[#123F13] mb-2">
            Praktisk information
          </h2>
          <p className="text-sm md:text-base text-[#3B352F]/80 mb-8 max-w-lg mx-auto">
            Her kan du læse mere om behandling, priser og de praktiske rammer.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-left">
            <Link 
              to="/behandlinger" 
              className="p-3.5 bg-white border border-[#E8DDCE] rounded-xl text-sm font-semibold text-[#123F13] hover:border-[#123F13] hover:shadow-sm transition-all flex items-center justify-between"
            >
              <span>Behandlinger</span>
              <span className="text-[#8C2F12]">›</span>
            </Link>
            <Link 
              to="/priser" 
              className="p-3.5 bg-white border border-[#E8DDCE] rounded-xl text-sm font-semibold text-[#123F13] hover:border-[#123F13] hover:shadow-sm transition-all flex items-center justify-between"
            >
              <span>Priser</span>
              <span className="text-[#8C2F12]">›</span>
            </Link>
            <Link 
              to="/online-sessioner" 
              className="p-3.5 bg-white border border-[#E8DDCE] rounded-xl text-sm font-semibold text-[#123F13] hover:border-[#123F13] hover:shadow-sm transition-all flex items-center justify-between"
            >
              <span>Online sessioner</span>
              <span className="text-[#8C2F12]">›</span>
            </Link>
            <Link 
              to="/faq" 
              className="p-3.5 bg-white border border-[#E8DDCE] rounded-xl text-sm font-semibold text-[#123F13] hover:border-[#123F13] hover:shadow-sm transition-all flex items-center justify-between"
            >
              <span>FAQ</span>
              <span className="text-[#8C2F12]">›</span>
            </Link>
            <Link 
              to="/kontakt" 
              className="p-3.5 bg-white border border-[#E8DDCE] rounded-xl text-sm font-semibold text-[#123F13] hover:border-[#123F13] hover:shadow-sm transition-all flex items-center justify-between"
            >
              <span>Kontakt</span>
              <span className="text-[#8C2F12]">›</span>
            </Link>
            <Link 
              to="/metakognitiv-terapi" 
              className="p-3.5 bg-white border border-[#E8DDCE] rounded-xl text-sm font-semibold text-[#123F13] hover:border-[#123F13] hover:shadow-sm transition-all flex items-center justify-between"
            >
              <span>Metakognitiv terapi</span>
              <span className="text-[#8C2F12]">›</span>
            </Link>
            <Link 
              to="/hypnose" 
              className="p-3.5 bg-white border border-[#E8DDCE] rounded-xl text-sm font-semibold text-[#123F13] hover:border-[#123F13] hover:shadow-sm transition-all flex items-center justify-between"
            >
              <span>Hypnose</span>
              <span className="text-[#8C2F12]">›</span>
            </Link>
            <Link 
              to="/om-lars" 
              className="p-3.5 bg-white border border-[#E8DDCE] rounded-xl text-sm font-semibold text-[#123F13] hover:border-[#123F13] hover:shadow-sm transition-all flex items-center justify-between"
            >
              <span>Om Lars</span>
              <span className="text-[#8C2F12]">›</span>
            </Link>
          </div>
        </div>
      </section>

      {/* EasyPractice Modal Overlay */}
      {isModalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 md:p-6 bg-black/65 backdrop-blur-sm animate-in fade-in duration-200"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-booking-title"
        >
          {/* Backdrop click dismiss */}
          <div 
            className="absolute inset-0 cursor-pointer" 
            onClick={closeBookingModal} 
            aria-hidden="true" 
          />

          {/* Modal Container */}
          <div className="relative z-10 w-full max-w-4xl max-h-[92vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-[#E8DDCE]">
            
            {/* Modal Header */}
            <div className="p-4 sm:p-5 bg-[#FAF7F2] border-b border-[#E8DDCE] flex items-center justify-between shrink-0">
              <div>
                <h3 id="modal-booking-title" className="text-lg sm:text-xl font-serif text-[#123F13] font-bold">
                  Book første behandlingssession
                </h3>
                <p className="text-xs sm:text-sm text-[#3B352F]/75 mt-0.5">
                  50 minutter · 375 kr. · Valby eller online · Introprisen gælder ikke rygestop
                </p>
              </div>
              <button
                type="button"
                onClick={closeBookingModal}
                className="w-9 h-9 rounded-full bg-white border border-[#E8DDCE] text-[#3B352F] hover:text-[#123F13] hover:border-[#123F13] flex items-center justify-center text-lg font-bold transition-all cursor-pointer shadow-sm"
                aria-label="Luk bookingvindue"
              >
                ×
              </button>
            </div>

            {/* Modal Body: EasyPractice iFrame */}
            <div className="flex-1 overflow-y-auto bg-white min-h-[500px] h-[75vh]">
              <iframe
                title="EasyPractice Online Booking - Lars Nissen Corell – Psykoterapeut MPF"
                style={{ width: '100%', height: '1000px' }}
                frameBorder="0"
                src="https://system.easypractice.net/book/lars-4"
              />
            </div>

            {/* Modal Footer / Fallback link */}
            <div className="p-3 bg-[#FAF7F2] border-t border-[#E8DDCE] text-center text-xs text-[#3B352F]/70 shrink-0">
              Hvis bookingvinduet ikke vises korrekt, kan du{' '}
              <a
                href="https://system.easypractice.net/book/lars-4"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#123F13] underline font-medium hover:text-[#123F13]/80"
              >
                åbne EasyPractice i et nyt vindue
              </a>
              .
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default BookTid;